"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertTriangle, Info, OctagonAlert } from "lucide-react";
import { CloseButton } from "@/components/ui/BackButton";
import { useSystemStatus } from "@/hooks/client/useSystemStatus";
import type { SystemStatusSeverity } from "@/lib/systemStatus";
import { cn } from "@/lib/utils";

/**
 * Custom property carrying the banner's rendered height. The header is
 * `fixed top-0` and `MainLayout` offsets content by the header's height, so a
 * banner cannot simply sit in the document flow — it would be painted over.
 * Instead the banner measures itself and publishes its height here; the header
 * and the content offset both read it. It stays `0px` whenever no banner is
 * shown, which makes the whole feature a no-op in the (overwhelmingly common)
 * healthy case.
 */
export const STATUS_BANNER_HEIGHT_VAR = "--status-banner-height";

const SESSION_DISMISS_KEY = "gondola.systemStatus.dismissed";

const bannerVariants = cva(
  "flex w-full items-center justify-center gap-3 px-4 py-2 text-sm lg:px-8",
  {
    variants: {
      severity: {
        info: "bg-muted text-foreground",
        warning: "bg-warning text-warning-foreground",
        critical: "bg-destructive text-destructive-foreground",
      },
    },
    defaultVariants: { severity: "warning" },
  }
);

const SEVERITY_ICONS: Record<SystemStatusSeverity, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  critical: OctagonAlert,
};

type BannerVariants = VariantProps<typeof bannerVariants>;

function readDismissed(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(SESSION_DISMISS_KEY);
  } catch {
    // sessionStorage throws in private mode / sandboxed iframes. Failing to read
    // a dismissal just means we show the banner, which is the safe direction.
    return null;
  }
}

function writeDismissed(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_DISMISS_KEY, key);
  } catch {
    // Non-sticky dismissal is acceptable; never let storage break the banner.
  }
}

/**
 * Site-wide outage / degraded-service banner, driven by the `system-status-banner`
 * PostHog flag so it can be toggled and reworded without a deploy.
 *
 * Dismissal is keyed on the message itself and scoped to the session: dismissing
 * one message never suppresses the *next* one, so a follow-up ("we're back") or an
 * escalation still reaches a user who closed the first.
 */
export function SystemStatusBanner() {
  const status = useSystemStatus();
  const ref = useRef<HTMLDivElement | null>(null);
  const [dismissedKey, setDismissedKey] = useState<string | null>(
    readDismissed
  );

  // Re-read after hydration: the lazy initializer runs during SSR (where there is
  // no window) and would otherwise leave a dismissed banner briefly visible.
  useEffect(() => {
    setDismissedKey(readDismissed());
  }, []);

  const messageKey = status ? `${status.severity}:${status.message}` : null;
  const isVisible = Boolean(messageKey) && messageKey !== dismissedKey;

  // Publish the measured height (and keep it correct as the message wraps across
  // breakpoints). useLayoutEffect so the offset lands before first paint rather
  // than after a visible jump.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = ref.current;

    if (!isVisible || !el) {
      root.style.setProperty(STATUS_BANNER_HEIGHT_VAR, "0px");
      return;
    }

    const publishHeight = () => {
      root.style.setProperty(
        STATUS_BANNER_HEIGHT_VAR,
        `${el.getBoundingClientRect().height}px`
      );
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.setProperty(STATUS_BANNER_HEIGHT_VAR, "0px");
    };
  }, [isVisible, messageKey]);

  const onDismiss = useCallback(() => {
    if (!messageKey) return;
    writeDismissed(messageKey);
    setDismissedKey(messageKey);
  }, [messageKey]);

  if (!status || !isVisible) return null;

  const Icon = SEVERITY_ICONS[status.severity];

  return (
    <div
      ref={ref}
      // Above the header's z-50 so the banner is never painted over.
      className="fixed inset-x-0 top-0 z-[60]"
      role="status"
      aria-live="polite"
    >
      <div
        className={cn(
          bannerVariants({
            severity: status.severity as BannerVariants["severity"],
          })
        )}
      >
        <Icon className="size-4 flex-shrink-0" aria-hidden="true" />
        <p className="min-w-0 text-center">
          {status.message}
          {status.learnMoreUrl ? (
            <>
              {" "}
              <a
                href={status.learnMoreUrl}
                className="underline underline-offset-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </>
          ) : null}
        </p>
        <CloseButton
          size="sm"
          ariaLabel="Dismiss status message"
          onClick={onDismiss}
          className="flex-shrink-0"
        />
      </div>
    </div>
  );
}

export default SystemStatusBanner;
